import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  FolderOpen as CaseIcon,
  Flag as IndicatorIcon,
  Computer as EntityIcon,
  Shield as DefenderIcon,
  AccountCircle,
  Logout,
  Settings,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  CreditCard as BillingIcon,
  Assessment as ReportsIcon,
  AdminPanelSettings as AdminIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePermission } from '../../hooks/usePermission';
import NotificationBell from '../Notifications/NotificationBell';
import GlobalSearch from '../Search/GlobalSearch';
import PartnerBanner from './PartnerBanner';
import BrandLogo from '../Brand/BrandLogo';

const drawerWidth = 240;

const baseMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Detections', icon: <SecurityIcon />, path: '/dashboard/detections', permission: ['detections', 'read'] },
  { text: 'Cases', icon: <CaseIcon />, path: '/dashboard/cases', permission: ['cases', 'read'] },
  { text: 'Indicators', icon: <IndicatorIcon />, path: '/dashboard/indicators', permission: ['indicators', 'read'] },
  { text: 'Entities', icon: <EntityIcon />, path: '/dashboard/entities', permission: ['entities', 'read'] },
  { text: 'Integrations', icon: <DefenderIcon />, path: '/dashboard/integrations', anyPermission: [['agents', 'read'], ['company', 'read']] },
];

const reportsMenuItem = { text: 'Reports', icon: <ReportsIcon />, path: '/dashboard/reports', permission: ['reports', 'read'] };
const billingMenuItem = { text: 'Billing', icon: <BillingIcon />, path: '/dashboard/billing', permission: ['billing', 'read'] };
const supportMenuItem = { text: 'Support', icon: <SupportIcon />, path: '/dashboard/support' };
const settingsMenuItem = { text: 'Settings', icon: <Settings />, path: '/dashboard/settings', anyPermission: [['company', 'read'], ['users', 'read'], ['roles', 'read']] };

export const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { hasPermission, hasAnyPermission, isPlatformAdmin } = usePermission();

  const allowed = (item) => {
    if (item.permission && !hasPermission(...item.permission)) return false;
    if (item.anyPermission && !hasAnyPermission(item.anyPermission)) return false;
    return true;
  };

  // Build menu items based on the user's permissions
  const menuItems = [
    ...baseMenuItems.filter(allowed),
    ...(allowed(reportsMenuItem) ? [reportsMenuItem] : []),
    ...(allowed(billingMenuItem) ? [billingMenuItem] : []),
    ...(allowed(settingsMenuItem) ? [settingsMenuItem] : []),
    supportMenuItem,
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar sx={{ justifyContent: 'center' }}>
        <BrandLogo height={40} onDark={isDark} sx={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={item.path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(item.path)}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.company?.name || ''}
          </Typography>

          <GlobalSearch />
          <NotificationBell />

          <IconButton
            color="inherit"
            sx={{ mr: 1 }}
            onClick={toggleTheme}
            title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>



          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {user?.firstName?.[0] || ''}{user?.lastName?.[0] || ''}
            </Avatar>
          </IconButton>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard/profile'); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            {isPlatformAdmin && (
              <MenuItem onClick={() => { handleMenuClose(); navigate('/admin'); }}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                Platform admin
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard/user-settings'); }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              User Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <PartnerBanner />
        {children}
      </Box>
    </Box>
  );
};
